import React, {FunctionComponent, useEffect, useState} from "react";
import {
    Box,
    ButtonGroup,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Table,
    TableBody,
    TableHead,
    TableRow, useTheme
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {
    MetadataCategoryData,
    MetadataData,
    useDeleteApiV1MetadataMutation,
    usePostApiV1MetadataMutation,
    usePutApiV1MetadataMutation
} from "../../../api/GovernanceToolApi";
import SearchIcon from "@mui/icons-material/Search";
import {SearchInfo} from "../../common/search-info-grid/SearchInfo";
import TableCell from "@mui/material/TableCell";
import {DateUtils} from "../../../book/DateUtils";
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {TableSkeleton} from "../../common/skeleton/TableSkeleton";
import {ErrorMessage} from "../../common/error-message/ErrorMessage";
import {
    ProcessStatusNotification,
    toProcessStatus
} from "../../common/process-status-notification/ProcessStatusNotification";
import {EditMetadataDialog} from "./EditMetadataDialog";
import {DeleteMetadataDialog} from "./DeleteMetadataDialog";

export const CategoryMetadataManagement: FunctionComponent<CategoryMetadataManagementProps> = props => {
    const {t} = useTranslation();
    const theme = useTheme();
    const [state, setState] = useState<CategoryMetadataManagementState>({
        search: '',
        currentPage: 0,
        pageSize: 50
    });
    const [searchMetadata, {
        data: metadataData,
        isLoading: isMetadataLoading,
        error: metadataError,
    }] = usePostApiV1MetadataMutation();
    const [deleteMetadata, {
        data: deleteMetadataData,
        isLoading: isDeleteMetadataLoading,
        error: deleteMetadataError,
        reset: resetDeleteMetadata
    }] = useDeleteApiV1MetadataMutation();
    const [editMetadata, {
        data: editMetadataData,
        isLoading: isEditMetadataLoading,
        error: editMetadataError,
        reset: resetEditMetadata
    }] = usePutApiV1MetadataMutation();

    const metadataValue = metadataData?.payload?.metadata ?? [];
    const metadataCount = metadataData?.payload?.count ?? 0;

    const callSearchMetadata = () => {
        return searchMetadata({
            searchMetadataRequest: {
                category: props.category.code,
                search: state.search.length ? state.search : null,
                pageSize: state.pageSize,
                page: state.currentPage
            }
        });
    };

    useEffect(() => {
        callSearchMetadata();
    }, [state.search, state.pageSize, state.currentPage, props.category.code]);

    const CategoryMetadataSearchInfoGrid = () => <SearchInfo
        count={metadataCount}
        currentPage={state.currentPage}
        pageSize={state.pageSize}
        onPageChange={page => {
            setState(state => ({...state, currentPage: page}));
        }}
        onPageSizeChange={pageSize => {
            setState(state => ({...state, pageSize}));
        }}
        paginationLabel={t("Products per page")}
        itemsLabel={t("products")}/>;

    return <Box sx={{padding: '1em'}}>
        <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="search-metadata">
                {t("metadataManagementSearch", {categoryCode: props.category?.code})}
            </InputLabel>
            <OutlinedInput
                id="search-metadata"
                value={state.search ?? ''}
                onChange={e => {
                    setState(state => ({...state, search: e.target.value}));
                }}
                label={t("metadataManagementSearch", {categoryCode: props.category?.code})}
                endAdornment={<InputAdornment position="end"><SearchIcon/></InputAdornment>}/>
        </FormControl>

        {isMetadataLoading &&
            <TableSkeleton sx={{marginTop: '35px'}}
                           rowCount={10}
                           colCount={5}
                           colCountSm={4}
                           colCountXs={2}
                           showPagination/>}

        {Boolean(metadataError) && <ErrorMessage
            message={t("An error occurred while loading metadata, please contact the administrator or try again")}/>}

        {Boolean(metadataValue.length) &&
            <Box>
                <CategoryMetadataSearchInfoGrid/>

                <Table aria-label="metadata">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("Value")}</TableCell>
                            <TableCell
                                sx={{[theme.breakpoints.down('sm')]: {display: 'none'}}}>{t("Created")}</TableCell>
                            <TableCell>{t("Last change")}</TableCell>
                            <TableCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {metadataValue.map(metadata => (
                            <TableRow key={metadata.id}>
                                <TableCell scope="row">
                                    {metadata.value}
                                </TableCell>
                                <TableCell scope="row" sx={{[theme.breakpoints.down('sm')]: {display: 'none'}}}>
                                    {DateUtils.dateTimeFormat(metadata.inserted)}
                                </TableCell>
                                <TableCell scope="row">
                                    {DateUtils.dateTimeFormat(metadata.updated)}
                                </TableCell>
                                <TableCell align="right">
                                    <ButtonGroup variant="outlined" color="primary" aria-label="action buttons">
                                        <IconButton aria-label={t("Edit").toLowerCase()}
                                                    title={t("Edit")}
                                                    color="primary"
                                                    onClick={() => {
                                                        setState(state => ({...state, metadataToEdit: metadata}))
                                                    }}>
                                            <EditIcon/>
                                        </IconButton>
                                        <IconButton aria-label={t("Remove").toLowerCase()}
                                                    title={t("Remove")}
                                                    color="primary"
                                                    onClick={() => {
                                                        setState(state => ({...state, metadataToDelete: metadata}))
                                                    }}>
                                            <DeleteForeverIcon/>
                                        </IconButton>
                                    </ButtonGroup>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <CategoryMetadataSearchInfoGrid/>
            </Box>}


        {state.metadataToDelete &&
            <DeleteMetadataDialog metadata={state.metadataToDelete}
                                  onCancelClick={() => {
                                      setState(state => ({
                                          ...state,
                                          metadataToDelete: undefined
                                      }));
                                  }}
                                  onOkClick={() => {
                                      if (state.metadataToDelete) {
                                          deleteMetadata({
                                              deleteMetadataRequest: {
                                                  metadataId: state.metadataToDelete.id
                                              }
                                          }).then(() => {
                                              if (state.currentPage !== 0) {
                                                  setState(state => ({...state, currentPage: 0}));
                                              } else {
                                                  callSearchMetadata();
                                              }

                                              resetDeleteMetadata();
                                          })
                                          setState(state => ({
                                              ...state,
                                              metadataToDelete: undefined
                                          }));
                                      }
                                  }}/>}

        {state.metadataToEdit &&
            <EditMetadataDialog metadata={state.metadataToEdit}
                                metadataCategories={props.categories}
                                onCancelClick={() => {
                                    setState(state => ({
                                        ...state,
                                        metadataToEdit: undefined
                                    }));
                                }}
                                onOkClick={(category, value) => {
                                    if (state.metadataToEdit) {
                                        editMetadata({
                                            updateMetadataRequest: {
                                                metadataId: state.metadataToEdit.id,
                                                category,
                                                value
                                            }
                                        }).then(() => {
                                            callSearchMetadata();

                                            resetEditMetadata();
                                        })
                                        setState(state => ({
                                            ...state,
                                            metadataToEdit: undefined
                                        }));
                                    }
                                }}/>}

        <ProcessStatusNotification
            status={toProcessStatus(isDeleteMetadataLoading, Boolean(deleteMetadataData?.payload), Boolean(deleteMetadataError))}
            onClose={() => resetDeleteMetadata()}
            onLoadingMessage={t("Deleting metadata please wait…")}
            onSuccessMessage={t("Metadata deleted successfully")}
            onErrorMessage={t("Metadata deleting failed")}/>

        <ProcessStatusNotification
            status={toProcessStatus(isEditMetadataLoading, Boolean(editMetadataData?.payload), Boolean(editMetadataError))}
            onClose={() => resetEditMetadata()}
            onLoadingMessage={t("Updating metadata please wait…")}
            onSuccessMessage={t("Metadata updated successfully")}
            onErrorMessage={t("Metadata updating failed")}/>
    </Box>
}

export interface CategoryMetadataManagementProps {
    category: MetadataCategoryData;
    categories: MetadataCategoryData[];
}

interface CategoryMetadataManagementState {
    search: string;
    currentPage: number;
    pageSize: number;
    metadataToDelete?: MetadataData;
    metadataToEdit?: MetadataData;
}